'use client';
import React, { useState, useEffect } from 'react';
import PartnerLayout from '@/components/PartnerLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ReferralLinkStat {
  plot_id: string | null;
  kavling_name: string | null;
  project_name: string | null;
  visits: number;
  leads: number;
  whatsapp_clicks: number;
  conversions: number;
  conversion_rate: number;
  last_activity: string | null;
  ref_url: string;
}

interface AgentProfile {
  id: string;
  agent_code: string | null;
  full_name: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Baru saja';
  if (mins < 60) return `${mins} mnt lalu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} jam lalu`;
  return `${Math.floor(hrs / 24)} hari lalu`;
}

function ConversionBar({ rate }: { rate: number }) {
  const pct = Math.min(rate, 100);
  const color = pct >= 20 ? 'bg-green-500' : pct >= 10 ? 'bg-yellow-500' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold tabular-nums ${pct >= 20 ? 'text-green-600' : pct >= 10 ? 'text-yellow-600' : 'text-red-500'}`}>
        {rate.toFixed(1)}%
      </span>
    </div>
  );
}

export default function ReferralPerformancePage() {
  const supabase = createClient();
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
  const [stats, setStats] = useState<ReferralLinkStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'visits' | 'leads' | 'conversion_rate'>('visits');
  const [filterProject, setFilterProject] = useState<string>('all');

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setIsLoading(false); return; }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('id, agent_code, full_name')
        .eq('id', user.id)
        .single();
      if (profile) setAgentProfile(profile as AgentProfile);

      const agentCode = profile?.agent_code;
      if (!agentCode) { setIsLoading(false); return; }

      // Fetch all referral clicks for this agent
      const { data: clicks } = await supabase
        .from('referral_clicks')
        .select('id, action_type, converted, created_at, kavling_name, project_name, plot_id')
        .or(`referrer_id.eq.${user.id},referral_code.eq.${agentCode}`)
        .order('created_at', { ascending: false });

      if (!clicks) { setIsLoading(false); return; }

      // Group by plot/kavling
      const grouped: Record<string, ReferralLinkStat> = {};

      // Add a "General" bucket for clicks without a specific plot
      const generalKey = '__general__';

      for (const click of clicks) {
        const key = click.plot_id || click.kavling_name || generalKey;
        if (!grouped[key]) {
          grouped[key] = {
            plot_id: click.plot_id || null,
            kavling_name: click.kavling_name || (key === generalKey ? 'Link Umum' : null),
            project_name: click.project_name || null,
            visits: 0,
            leads: 0,
            whatsapp_clicks: 0,
            conversions: 0,
            conversion_rate: 0,
            last_activity: click.created_at,
            ref_url: click.plot_id
              ? `https://betaland.id/kavling/${click.plot_id}?ref=${agentCode}`
              : `https://betaland.id?ref=${agentCode}`,
          };
        }
        if (click.action_type === 'visit') grouped[key].visits++;
        if (click.action_type === 'form') grouped[key].leads++;
        if (click.action_type === 'whatsapp') grouped[key].whatsapp_clicks++;
        if (click.converted) grouped[key].conversions++;
        if (!grouped[key].last_activity || click.created_at > grouped[key].last_activity!) {
          grouped[key].last_activity = click.created_at;
        }
      }

      // Compute conversion rate
      const result = Object.values(grouped).map((s) => ({
        ...s,
        conversion_rate: s.visits > 0 ? (s.leads / s.visits) * 100 : 0,
      }));

      setStats(result);
      setIsLoading(false);
    };
    load();
  }, []);

  const agentCode = agentProfile?.agent_code || 'AGT-000';

  const projects = ['all', ...Array.from(new Set(stats.map((s) => s.project_name).filter(Boolean) as string[]))];

  const filtered = stats
    .filter((s) => filterProject === 'all' || s.project_name === filterProject)
    .sort((a, b) => b[sortBy] - a[sortBy]);

  const topConverter = [...stats].sort((a, b) => b.conversion_rate - a.conversion_rate)[0];
  const totalVisits = stats.reduce((s, r) => s + r.visits, 0);
  const totalLeads = stats.reduce((s, r) => s + r.leads, 0);
  const overallConversion = totalVisits > 0 ? (totalLeads / totalVisits) * 100 : 0;

  const handleCopy = (url: string, id: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleShare = (url: string, name: string) => {
    const text = `Halo! Cek properti menarik di Betaland.ID 👇\n${url}`;
    if (navigator.share) {
      navigator.share({ title: name, text, url }).catch(() => {});
    } else {
      const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(wa, '_blank');
    }
  };

  return (
    <PartnerLayout title="Performa Referral" subtitle="Analisis link referral per properti">
      <div className="p-4 lg:p-6 xl:p-8 max-w-screen-xl mx-auto space-y-6">

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total Link Aktif', value: stats.length, icon: 'LinkIcon', color: 'text-purple', bg: 'bg-purple/10' },
            { label: 'Total Kunjungan', value: totalVisits, icon: 'EyeIcon', color: 'text-blue', bg: 'bg-blue/10' },
            { label: 'Total Lead', value: totalLeads, icon: 'UserGroupIcon', color: 'text-secondary', bg: 'bg-secondary/10' },
            { label: 'Konversi Rata-rata', value: `${overallConversion.toFixed(1)}%`, icon: 'ArrowTrendingUpIcon', color: 'text-accent', bg: 'bg-accent/20' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-card rounded-xl border border-border p-4">
              <div className={`w-9 h-9 rounded-xl ${kpi.bg} flex items-center justify-center mb-3`}>
                <Icon name={kpi.icon as any} size={18} className={kpi.color} />
              </div>
              <p className="text-2xl font-bold text-foreground">{isLoading ? '—' : kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Top Converting Property */}
        {!isLoading && topConverter && topConverter.conversion_rate > 0 && (
          <div className="bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20 rounded-xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Icon name="TrophyIcon" size={20} className="text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-accent uppercase tracking-wide mb-0.5">🏆 Top Converting Property</p>
              <p className="font-bold text-foreground truncate">{topConverter.kavling_name || 'Link Umum'}</p>
              <p className="text-xs text-muted-foreground">{topConverter.project_name || '—'}</p>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className="text-xs text-muted-foreground">{topConverter.visits} kunjungan</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground">{topConverter.leads} lead</span>
                <span className="text-xs font-bold text-green-600">{topConverter.conversion_rate.toFixed(1)}% konversi</span>
              </div>
            </div>
            <button
              onClick={() => handleCopy(topConverter.ref_url, 'top')}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white rounded-lg text-xs font-medium hover:bg-accent/90 transition-colors"
            >
              <Icon name={copiedId === 'top' ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={13} />
              {copiedId === 'top' ? 'Tersalin!' : 'Salin Link'}
            </button>
          </div>
        )}

        {/* Filters & Sort */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 bg-card border border-border rounded-xl px-3 py-2 flex-1 min-w-[160px]">
            <Icon name="FunnelIcon" size={14} className="text-muted-foreground flex-shrink-0" />
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="bg-transparent text-sm text-foreground flex-1 outline-none"
            >
              {projects.map((p) => (
                <option key={p} value={p}>{p === 'all' ? 'Semua Proyek' : p}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1 bg-card border border-border rounded-xl p-1">
            {([
              { key: 'visits', label: 'Kunjungan' },
              { key: 'leads', label: 'Lead' },
              { key: 'conversion_rate', label: 'Konversi' },
            ] as const).map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  sortBy === opt.key ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Referral Links Table */}
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                <div className="h-3 bg-muted rounded w-1/4 mb-3" />
                <div className="h-2 bg-muted rounded w-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-10 text-center">
            <Icon name="LinkIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="font-semibold text-foreground mb-1">Belum ada data referral</p>
            <p className="text-sm text-muted-foreground">Mulai bagikan link referral Anda ke calon pembeli untuk melihat statistik di sini.</p>
            <button
              onClick={() => handleCopy(`https://betaland.id?ref=${agentCode}`, 'empty')}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="ClipboardDocumentIcon" size={15} />
              Salin Link Utama
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((row, idx) => {
              const rowId = row.plot_id || row.kavling_name || `row-${idx}`;
              const isCopied = copiedId === rowId;
              const rankIcon = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
              return (
                <div key={rowId} className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {rankIcon && <span className="text-base leading-none">{rankIcon}</span>}
                        <p className="font-semibold text-foreground truncate">
                          {row.kavling_name || 'Link Umum'}
                        </p>
                        {row.project_name && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                            {row.project_name}
                          </span>
                        )}
                      </div>
                      {row.last_activity && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Aktivitas terakhir: {timeAgo(row.last_activity)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleCopy(row.ref_url, rowId)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          isCopied ? 'bg-green-100 text-green-700' : 'bg-muted text-foreground hover:bg-muted/80'
                        }`}
                        title="Salin link"
                      >
                        <Icon name={isCopied ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={13} />
                        <span className="hidden sm:inline">{isCopied ? 'Tersalin!' : 'Salin'}</span>
                      </button>
                      <button
                        onClick={() => handleShare(row.ref_url, row.kavling_name || 'Betaland.ID')}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors"
                        title="Bagikan"
                      >
                        <Icon name="ShareIcon" size={13} />
                        <span className="hidden sm:inline">Share</span>
                      </button>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      { label: 'Kunjungan', value: row.visits, icon: 'EyeIcon', color: 'text-blue' },
                      { label: 'Lead', value: row.leads, icon: 'UserPlusIcon', color: 'text-secondary' },
                      { label: 'WhatsApp', value: row.whatsapp_clicks, icon: 'ChatBubbleLeftRightIcon', color: 'text-green-600' },
                      { label: 'Closing', value: row.conversions, icon: 'CheckBadgeIcon', color: 'text-accent' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-muted/50 rounded-lg p-2 text-center">
                        <Icon name={stat.icon as any} size={14} className={`${stat.color} mx-auto mb-0.5`} />
                        <p className="text-base font-bold text-foreground leading-tight">{stat.value}</p>
                        <p className="text-xs text-muted-foreground leading-tight">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Conversion bar */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Tingkat Konversi (Visit → Lead)</span>
                    </div>
                    <ConversionBar rate={row.conversion_rate} />
                  </div>

                  {/* Link preview */}
                  <div className="mt-3 flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2">
                    <Icon name="LinkIcon" size={12} className="text-muted-foreground flex-shrink-0" />
                    <span className="text-xs text-muted-foreground truncate font-mono">{row.ref_url}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* General referral link card */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="GlobeAltIcon" size={18} className="text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm">Link Referral Utama</p>
              <p className="text-xs text-muted-foreground">Berlaku untuk semua properti</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2 mb-3">
            <span className="text-xs text-foreground font-mono flex-1 truncate">
              https://betaland.id?ref={agentCode}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleCopy(`https://betaland.id?ref=${agentCode}`, 'main')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                copiedId === 'main' ? 'bg-green-100 text-green-700' : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              <Icon name={copiedId === 'main' ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={15} />
              {copiedId === 'main' ? 'Tersalin!' : 'Salin Link Utama'}
            </button>
            <button
              onClick={() => handleShare(`https://betaland.id?ref=${agentCode}`, 'Betaland.ID')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-muted text-foreground rounded-xl text-sm font-medium hover:bg-muted/80 transition-colors"
            >
              <Icon name="ShareIcon" size={15} />
              Share
            </button>
          </div>
        </div>

      </div>
    </PartnerLayout>
  );
}
